import { X } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Sheet, type SheetRef } from 'react-modal-sheet'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type BottomSheetProps = {
  children: React.ReactNode
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
  /** 背景オーバーレイの色 (例: 'transparent', 'rgba(0,0,0,0.5)') */
  overlayColor?: string
  /** 背景オーバーレイを非表示にする */
  noOverlay?: boolean
  /**
   * スナップポイントの配列 (例: [0.5, 1] = 50%と100%)
   * 指定すると複数の高さにスナップできる
   */
  snapPoints?: (number | string)[]
  /** 初期表示時のスナップポイント */
  defaultSnapPoint?: number | string
  /** 閉じたときも子コンポーネントをマウントしたままにする */
  keepMounted?: boolean
  /** シートの外側をクリックしても閉じないようにする */
  dismissible?: boolean
  /** ドラッグで完全に閉じることを無効化する */
  disableDismiss?: boolean
  /** タイトルバー（タイトル + 閉じるボタン）を非表示にし、ドラッグハンドルのみにする */
  hideTitleBar?: boolean
  /** タイトルバーの代わりに置く要素。スクロールしない位置に固定される */
  headerContent?: React.ReactNode
  /** コンテンツを包むラッパーに適用するクラス（例: 'h-full' で内容をシート全高に広げる） */
  contentClassName?: string
  /** スクロール領域の外側、シート下端に固定表示する要素 */
  footer?: React.ReactNode
  /** キーボードが出ているあいだ footer をキーボードの上に持ち上げる（入力しながら押すボタンを置くとき用） */
  footerAboveKeyboard?: boolean
  /**
   * 閉じないシートにする。snapPoints をそのまま使い、0（閉じる）と 1（全高）を足さない。
   * 背景の地図を触れるまま下に居座らせる用途を想定している
   */
  persistent?: boolean
}

export type BottomSheetRef = {
  /** 指定したスナップポイント値にスナップする */
  snapTo: (snapPointValue: number) => void
}

export const BottomSheet = forwardRef<BottomSheetRef, BottomSheetProps>(function BottomSheet(
  {
    children,
    title,
    open,
    onOpenChange,
    className,
    overlayColor,
    noOverlay = false,
    snapPoints,
    defaultSnapPoint,
    keepMounted = false,
    dismissible = true,
    disableDismiss = false,
    hideTitleBar = false,
    headerContent,
    contentClassName,
    footer,
    footerAboveKeyboard = false,
    persistent = false,
  },
  ref,
) {
  const sheetRef = useRef<SheetRef>(null)
  const [hasBeenOpened, setHasBeenOpened] = useState(open)
  const childrenRef = useRef<React.ReactNode>(null)
  const [sheetY, setSheetY] = useState(0)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  // キーボードの上端から画面下端までの高さ（表示領域が上にずれた分も引く）
  const [keyboardInset, setKeyboardInset] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const sheetSnapPoints = useMemo(() => {
    const baseSnapPoints = snapPoints ? snapPoints.map((sp) => Number(sp)) : [0.7, 1]
    if (persistent) {
      return [...baseSnapPoints].sort((a, b) => a - b)
    }
    if (!baseSnapPoints.includes(1)) {
      baseSnapPoints.push(1)
    }
    const sorted = baseSnapPoints.sort((a, b) => a - b)
    return [0, ...sorted.filter((p) => p > 0)]
  }, [snapPoints, persistent])

  useImperativeHandle(
    ref,
    () => ({
      snapTo: (snapPointValue: number) => {
        const index = sheetSnapPoints.indexOf(snapPointValue)
        if (index >= 0) {
          sheetRef.current?.snapTo(index)
        }
      },
    }),
    [sheetSnapPoints],
  )

  const defaultIndex = useMemo(() => {
    return defaultSnapPoint ? sheetSnapPoints.indexOf(Number(defaultSnapPoint)) : 1
  }, [defaultSnapPoint, sheetSnapPoints])

  useEffect(() => {
    if (open && !hasBeenOpened) {
      setHasBeenOpened(true)
    }
  }, [open, hasBeenOpened])

  useEffect(() => {
    if (open || !childrenRef.current) {
      childrenRef.current = children
    }
  }, [open, children])

  useEffect(() => {
    const unsubscribe = sheetRef.current?.y.on('change', (latest) => {
      setSheetY(latest)
    })
    return unsubscribe
  }, [open])

  // モバイルキーボード表示時にパディングを追加してスクロール可能にする
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport || !open) return

    const handleResize = () => {
      const diff = window.innerHeight - viewport.height
      setKeyboardHeight(diff > 100 ? diff : 0)
    }

    viewport.addEventListener('resize', handleResize)
    return () => viewport.removeEventListener('resize', handleResize)
  }, [open])

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport || !open || !footerAboveKeyboard) return

    const update = () => {
      const inset = window.innerHeight - viewport.height - viewport.offsetTop
      setKeyboardInset(inset > 100 ? inset : 0)
    }

    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    // キーボードが出たまま開いたときは resize が来ないので、今の状態を一度読んでおく
    update()
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      // キーボードが閉じる前にシートが閉じると resize を受け取れないので、次に開いたときに残らないよう戻す
      setKeyboardInset(0)
    }
  }, [open, footerAboveKeyboard])

  // passive touchstart リスナーを追加して警告を回避
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleTouchStart = () => {
      // 何もしない - passive リスナーとして登録するだけ
    }

    // passive: true を指定して警告を回避
    container.addEventListener('touchstart', handleTouchStart, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
    }
  }, [open])

  const shouldRender = keepMounted ? hasBeenOpened : open
  const renderedChildren = keepMounted ? childrenRef.current : children

  // ドラッグで引き下げた分だけスクロール領域の下に余白を足して、隠れた内容まで届くようにする。
  // ただし footer がある場合は footer 側を持ち上げるため余白は不要で、
  // 足すと内容ボックスが縮んで背景色の帯が見えてしまう。
  // footer をキーボードの上に持ち上げるときは、内容の領域がその分縮むので余白は足さない
  const liftFooter = !!footer && footerAboveKeyboard
  const scrollPaddingBottom = (footer ? 0 : sheetY) + (liftFooter ? 0 : keyboardHeight)
  // シートは全高の一部（snapPoints）で止まり、下端は画面外に出ている。
  // footer を見えている範囲の下端に置くため、その分だけ持ち上げる（閉じる動きの間は一緒に下がる）
  const footerOffset = (open ? sheetY : 0) + (liftFooter ? keyboardInset : 0)

  if (!shouldRender) {
    return null
  }

  return (
    <Sheet
      ref={sheetRef}
      isOpen={open}
      onClose={() => onOpenChange(false)}
      snapPoints={sheetSnapPoints}
      initialSnap={defaultIndex >= 0 ? defaultIndex : 0}
      disableDismiss={disableDismiss}
    >
      <div ref={containerRef}>
        <Sheet.Container className={cn('bg-background! rounded-t-2xl!', className)}>
          <Sheet.Header className="bg-background! rounded-t-2xl!">
            {/* ドラッグハンドル */}
            <div className="bg-muted mx-auto mt-4 h-2 w-25 shrink-0 rounded-full" />
            {headerContent}
            {/* タイトルバー */}
            {!hideTitleBar && !headerContent && (
              <div className="flex flex-row justify-between items-center px-4 border-b border-border">
                <h2 className="font-semibold text-lg">{title}</h2>
                <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="p-4!">
                  <X className="h-5 w-8" />
                </Button>
              </div>
            )}
          </Sheet.Header>
          <Sheet.Content
            className="bg-background!"
            scrollStyle={{ paddingBottom: scrollPaddingBottom, overflowX: 'hidden' }}
            disableDrag
          >
            <div className={cn('space-y-6', contentClassName)}>{renderedChildren}</div>
          </Sheet.Content>
          {footer && (
            <div className="shrink-0" style={{ marginBottom: footerOffset }}>
              {footer}
            </div>
          )}
        </Sheet.Container>
      </div>
      {!noOverlay && (
        <Sheet.Backdrop
          style={overlayColor ? { backgroundColor: overlayColor } : undefined}
          onTap={() => dismissible && onOpenChange(false)}
        />
      )}
    </Sheet>
  )
})
